import type { AppSpacesConfigsPersistence, LayoutSection, LayoutSpec } from '../../../types/LayoutConfig';
import { VALID_SECTIONS } from '../../../types/LayoutConfig';
import { addTypeToLayoutPanelParams } from '../addTypeToLayoutPanelParams';
import {
  SPACES_NAV_LAYOUT_MIGRATION_VERSION,
  shouldRunSpacesNavLayoutMigration,
} from '../spacesNavLayoutMigrationVersion';
import type { AppConfigStateWithLegacyLayoutConfigs } from './legacyLayoutConfigs.types';

/**
 * Normalizes `layoutConfigs[section]` from legacy dict to array, same as the former migrateLayoutConfigsToArray.
 */
function normalizeLayoutConfigsSectionToArray(sectionData: unknown): LayoutSpec[] | undefined {
  if (sectionData == null) {
    return undefined;
  }
  if (Array.isArray(sectionData)) {
    return sectionData as LayoutSpec[];
  }
  if (typeof sectionData === 'object') {
    return Object.entries(sectionData as Record<string, LayoutSpec>).map(([key, value]) => ({
      ...(value as unknown as Record<string, unknown>),
      id: key,
    })) as LayoutSpec[];
  }
  return undefined;
}

/** Keeps `group` on each spec when set; order is the `layouts` array order. */
function layoutsFromLegacyRaw(rawLayouts: LayoutSpec[]): LayoutSpec[] {
  return rawLayouts.map(raw => {
    const { group: legacyGroup, ...rest } = raw;
    const base = rest as LayoutSpec;
    return legacyGroup != null && legacyGroup !== '' ? { ...base, group: legacyGroup } : base;
  });
}

/**
 * Copies legacy top-level `layoutConfigs[section]` into `spacesConfigs[section].layouts` and persists `group` on each `LayoutSpec`.
 * Does not remove `layoutConfigs` (non-destructive). When `SPACES_NAV_LAYOUT_MIGRATION_VERSION` is incremented,
 * re-applies from preserved legacy data. Idempotent when `spacesConfigs.spacesNavLayoutMigrationVersion` is current and
 * `spacesConfigs[section].layouts` is already populated.
 */
export function migrateSpacesNavFromLayoutConfigs(config: AppConfigStateWithLegacyLayoutConfigs): void {
  const layoutConfigs = config.layoutConfigs;
  const prevRoot = (config.spacesConfigs ?? {}) as AppSpacesConfigsPersistence;
  const nextRoot: AppSpacesConfigsPersistence = {
    trading: prevRoot.trading ?? { initialized: false },
    monitoring: prevRoot.monitoring ?? { initialized: false },
    intel: prevRoot.intel ?? { initialized: false },
    dealer: prevRoot.dealer ?? { initialized: false },
    spacesNavLayoutMigrationVersion: prevRoot.spacesNavLayoutMigrationVersion,
  };

  const needsLegacyReapply = shouldRunSpacesNavLayoutMigration(config);

  for (const section of VALID_SECTIONS) {
    const layoutSection = section as LayoutSection;
    const legacy = layoutConfigs?.[layoutSection];
    const existingSection = nextRoot[layoutSection] ?? { initialized: false };

    if (legacy == null) {
      continue;
    }

    if (!needsLegacyReapply && existingSection.layouts != null && existingSection.layouts.length > 0) {
      continue;
    }

    const rawLayouts = normalizeLayoutConfigsSectionToArray(legacy);
    if (rawLayouts == null || rawLayouts.length === 0) {
      continue;
    }

    const layouts = layoutsFromLegacyRaw(rawLayouts);

    nextRoot[layoutSection] = {
      ...existingSection,
      layouts,
    };
  }

  nextRoot.spacesNavLayoutMigrationVersion = SPACES_NAV_LAYOUT_MIGRATION_VERSION;
  config.spacesConfigs = nextRoot;

  const tradingSection = config.spacesConfigs.trading;
  if (tradingSection?.layouts != null) {
    tradingSection.layouts = tradingSection.layouts.map((spec: any) =>
      spec?.dockViewLayout != null ? addTypeToLayoutPanelParams(spec) : spec
    );
  }
}
